/**
 * Helpful loop method for iterating over nodelists
 * 
 * @export
 * @param {string} section 
 * @returns {function} 
 */
export function forEach(array: NodeList | any[] | DOMTokenList, callback: Function, scope?: any) {
    for (let i = 0; i < array.length; i++) {
        callback.call(scope, i, array[i]);
    }
}


/**
 * Element selector, returns a single element.
 * 
 * @param selector The selector string
 */
export function get(selector: string, context: Element | HTMLElement | Document = document) : Element | undefined {
    if(!selector) return;
    
    if(selector.substr(0, 1) === '#') {
        return <Element>document.getElementById(selector.substr(1, selector.length - 1));
    } else {
        const element = context.querySelector(selector);
        if(element) {
            return element;
        } else {
            return undefined;
        }
    }
}


/**
 * Removes the last segment from the URL
 * 
 * @param url The URL string to update
 */
export function removeLastURLSegment(url = window.location.href): string {
    const urlSegments = url.split('/');
    urlSegments.pop();
    if(url[url.length - 1] === '/') urlSegments.pop();
    return( urlSegments.join('/') );
}


/**
 * Element selector, returns an array of elements.
 * 
 * @param selector The selector string
 */
export function getAll(selector: string, context: Element | HTMLElement | DocumentFragment | Document = document) : Element[] | undefined {
    if(!selector) return;
    
    const elements = context.querySelectorAll(selector);
    if(elements.length) {
        return [].slice.call(elements);
    } else {
        return undefined;
    }
}


/**
 * Create a cross browser event constructor
 * 
 * @param eventName Our event
 */
export function createNewEvent(eventName: string) {
    let event;
    if (typeof(Event) === 'function') {
        event = new Event(eventName);
    } else {
        event = document.createEvent('Event');
        event.initEvent(eventName, true, true);
    }
    return event;
}


/**
 * Converts an object to an array
 * 
 * @param any The iterable to conver to an rray 
 */
export function toArray(iterable: any): any[] {
    return [].slice.call(iterable);
}


/**
 * Returns the value of the provided query string
 *
 * @export
 * @param {string} name
 * @param {string} url
 * @returns {(string | null)}
 */
export function getParameterByName(name: string, url: string = window.location.href): string | null {
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`), results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}


/**
 * Returns true if the current device supports touch events.
 * 
 * @return boolean;
 */
export function isTouchDevice(): boolean {
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    const query    = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
    const mq = function() {
      return window.matchMedia(query).matches;
    }
  
    // @ts-ignore
    if (('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch) {
      return true;
    }
  
    return mq();
}


/**
 * Inserts the query parameter
 * 
 * @param key The query parameter name
 * @param value The query parameter value
 */
export function insertParam(key: string, value: string) {
    key       = encodeURI(key);
    value     = encodeURI(value);
    const kvp = document.location.search.substr(1).split('&');

    let i = kvp.length;
    let x;
    while(i--)  {
        x = kvp[i].split('=');
        if (x[0] === key) {
            x[1]   = value;
            kvp[i] = x.join('=');
            break;
        }
    }

    if ( i < 0 ) {
        kvp[kvp.length] = [key,value].join('=');
    }

    return kvp.join('&');
}


/**
 * Removes a query parameter from a URL string
 * 
 * @param parameter The parameter to remove
 * @param url The URL string to modify
 */
export function removeURLParameter(key: string, url: string = window.location.href) {
    return url.replace(`?${key}`, '?').replace(`&${key}`, '');
}


/**
 * Returns the height of the element without it's vertical padding or border
 * 
 * @param element The element to measure
 */
export function getInnerHeight(element: HTMLElement): number {
    const styles        = getComputedStyle(element);
    const paddingHeight = parseInt(styles.paddingTop as string, 10) + parseInt(styles.paddingBottom as string, 10);
    const borderHeight  = parseInt(styles.borderTopWidth as string, 10) + parseInt(styles.borderBottomWidth as string, 10);
    const height        = parseInt(styles.height as string, 10);

    return height - paddingHeight - borderHeight;
}


/**
 * Returns true if the screen width matches the requested breakpoint.
 * 
 * @param breakpoint The breakpoint to match
 */
export function isBreakpoint(breakpoint: string, minOrMax = 'max'): boolean {

    interface Breakpoints {
        mobile: string;
        tablet: string;
        desktop: string;
        wide: string;
        [key: string]: string;
    }

    const breakpoints: Breakpoints = {
        mobile: `(${ minOrMax }-width: 320px)`,
        tablet: `(${ minOrMax }-width: 740px)`,
        desktop: `(${ minOrMax }-width: 980px)`,
        wide: `(${ minOrMax }-width: 1300px)`
    }

    return window.matchMedia(breakpoints[breakpoint]).matches;
}
